import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../lib/auth'

export default function WelcomeModal({ onClose, daysRemaining }) {
  const navigate = useNavigate()
  const { profile } = useAuthStore()
  const [copied, setCopied] = useState(false)

  const hiremailAlias = profile?.username ? `${profile.username}@hiremail.net` : null

  const copyAlias = () => {
    if (hiremailAlias) {
      navigator.clipboard.writeText(hiremailAlias)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0d1321] border border-white/10 rounded-2xl p-8 max-w-lg w-full shadow-2xl animate-fade-in">

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-white transition text-xl w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10"
        >
          &times;
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-cyan-500/15 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-cyan-500/20">
            <svg className="w-8 h-8 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-white mb-1">
            You're protected.
          </h3>
          <p className="text-gray-400 text-sm">
            Welcome to ScamView. Here's how to get the most out of your account.
          </p>
        </div>

        {/* Trial banner */}
        {daysRemaining > 0 && (
          <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-3 mb-5 text-center">
            <p className="text-cyan-400 font-semibold text-sm">
              Free Trial Active &mdash; {daysRemaining} days of Premium features included
            </p>
          </div>
        )}

        {/* HireMail alias — hero callout */}
        {hiremailAlias && (
          <div className="bg-gradient-to-br from-cyan-900/30 to-cyan-800/10 border border-cyan-500/25 rounded-xl p-4 mb-5">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="text-cyan-400 font-bold text-sm">Your Protected Inbox Address</span>
            </div>
            <p className="text-gray-400 text-xs mb-3 leading-relaxed">
              Use this address on your resume and job boards. Every recruiter email sent here is automatically scanned before it reaches you.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-black/30 text-cyan-300 text-sm px-3 py-2 rounded-lg font-mono border border-cyan-500/20 truncate">
                {hiremailAlias}
              </code>
              <button
                onClick={copyAlias}
                className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                  copied
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30'
                }`}
              >
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-2.5 mb-5">
          <button
            onClick={() => onClose()}
            className="w-full p-4 rounded-xl border border-cyan-500/20 bg-cyan-500/5 hover:bg-cyan-500/15 transition text-left group"
          >
            <span className="text-cyan-400 font-semibold text-sm">Run Your First Scan</span>
            <span className="text-gray-500 text-xs block mt-0.5">
              Paste a recruiter email, phone number, or message to check for fraud.
            </span>
          </button>

          <button
            onClick={() => { onClose(); navigate('/how-to') }}
            className="w-full p-4 rounded-xl border border-purple-500/20 bg-purple-500/5 hover:bg-purple-500/15 transition text-left group"
          >
            <span className="text-purple-400 font-semibold text-sm">How ScamView Works</span>
            <span className="text-gray-500 text-xs block mt-0.5">
              Learn how to use your HireMail alias and get the most from every scan.
            </span>
          </button>

          <button
            onClick={() => { onClose(); navigate('/settings') }}
            className="w-full p-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition text-left group"
          >
            <span className="text-white font-semibold text-sm">Account Settings</span>
            <span className="text-gray-500 text-xs block mt-0.5">
              Update your profile, billing, and notification preferences.
            </span>
          </button>
        </div>

        <p className="text-center text-gray-600 text-xs">
          You won't see this again. Find your HireMail address anytime on your dashboard.
        </p>
      </div>
    </div>
  )
}
